import getUrlParameter from '@rsos/base-utils/getUrlParameter';
import { SCORPIUS_KEY, PRODUCT_TOKEN } from '../../actions/types';

/**
 * Backwards-compatible mitigation for legacy auto-login links that still pass
 * `access_token` in the query string.
 *
 * We intentionally keep consuming that token so existing entry points do not
 * break, but we immediately remove only the sensitive `access_token` parameter
 * from the visible URL after it has been read. This reduces accidental token
 * disclosure through copied links, screenshots, browser history, and referrer
 * headers while preserving the current login flow. Other query params and the
 * hash fragment are preserved.
 */
const stripAccessTokenFromUrl = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.history?.replaceState !== 'function'
  ) {
    return;
  }

  const url = new URL(window.location.href);

  if (!url.searchParams.has('access_token')) {
    return;
  }

  url.searchParams.delete('access_token');

  // Security context: the token may arrive through a legacy deep link, but it
  // should not remain in the address bar any longer than necessary because URL
  // tokens are easy to leak outside the app boundary.
  window.history.replaceState(
    {},
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

/**
 * Marks tokens that came from a `?access_token=` link rather than a login.
 *
 * The flow is the legacy auto-login deep link: a URL with a live access token
 * in the query string, e.g. `/home?access_token=<jwt>`, which signs the user in
 * with no login screen. It is issued no refresh token, so the session lasts
 * exactly as long as that token and then ends. `getTokens` consumes the
 * parameter once and `stripAccessTokenFromUrl` scrubs it from the address bar,
 * as a defense-in-depth measure against URL-based credential exposure.
 *
 * Recorded at the moment of the bootstrap because that is the only moment it is
 * knowable: the query parameter is stripped from the URL immediately after, and
 * from then on such a session is indistinguishable in storage from an ordinary
 * one that lost its refresh token. The difference matters — one is working as
 * designed and the other is a fault — and it decides whether running out of
 * access token is reported to Sentry.
 *
 * Note that these sessions land on the ordinary `/home` and `/query-interface`
 * routes rather than anywhere identifiable, so a URL breakdown cannot be used
 * to separate them from real logins.
 */
export const AUTH_SOURCE_URL_ACCESS_TOKEN = 'url-access-token';

/**
 * Marks a session minted from an IRP or ICSP token — `?irp_token=`,
 * `?icsp_token=`, or the decrypted token those leave behind in the persisted
 * `irp` reducer.
 *
 * Separate from the `?access_token=` marker above because these never pass
 * through the bootstrap in `getTokens`: the IRP token is decrypted first and
 * the resulting session token reaches storage through `setTokens`, so the
 * entry points have to record it themselves. The two are kept distinct rather
 * than merged into one value so the split stays readable in storage and in
 * anything that reports on it; both mean the same thing to callers, which is
 * what `isBootstrappedSession` answers.
 */
export const AUTH_SOURCE_IRP = 'irp';

const REFRESHLESS_AUTH_SOURCES = [
  AUTH_SOURCE_URL_ACCESS_TOKEN,
  AUTH_SOURCE_IRP,
];

/**
 * Parses whatever is in storage, with none of the bootstrap behavior in
 * `getTokens`. `setTokens` needs to look at the stored session, and going
 * through `getTokens` to do it would let a write trigger a bootstrap — which
 * persists a token and strips the query parameter, both surprising from a
 * setter.
 * @returns {Object|null}
 */
const parseStoredTokens = () => {
  const tokenString = localStorage.getItem(SCORPIUS_KEY);
  if (!tokenString) {
    return null;
  }
  try {
    return JSON.parse(tokenString);
  } catch (error) {
    localStorage.removeItem(SCORPIUS_KEY);
    return null;
  }
};

/**
 * Returns the Scorpius tokens from localStorage.
 * Existing stored tokens still take precedence. When storage is empty, legacy
 * auto-login links may bootstrap auth from `?access_token=...`; in that case
 * the token is consumed once and then scrubbed from the URL as a defense-in-
 * depth measure against URL-based credential exposure.
 * @returns {Object|null} tokens
 */
export const getTokens = () => {
  const tokenString = localStorage.getItem(SCORPIUS_KEY);
  const accessToken = getUrlParameter('access_token');
  if (!tokenString && accessToken) {
    const bootstrapTokens = {
      refresh_token: null,
      token: accessToken,
      auth_source: AUTH_SOURCE_URL_ACCESS_TOKEN,
    };
    // Persist the bootstrap token immediately because getTokens() runs while
    // the Redux store initializes, before app-level initialize() handlers run.
    // Without this, stripping the query param can leave downstream apps with
    // no token source by the time they call fetchUser().
    localStorage.setItem(SCORPIUS_KEY, JSON.stringify(bootstrapTokens));
    stripAccessTokenFromUrl();
    // Returned rather than re-read, so a storage write that throws (private
    // browsing, quota) still yields a usable token for this page load.
    return bootstrapTokens;
  }
  return parseStoredTokens();
};

/**
 * Whether this session was bootstrapped rather than logged into — an IRP or
 * ICSP token, or a legacy `?access_token=` auto-login link. Such a session is
 * issued no refresh token on purpose, so reaching the end of its access token
 * is expected rather than broken.
 *
 * A session that later obtains real credentials drops the marker, because
 * `setTokens` only carries it forward while the incoming tokens still have no
 * usable refresh token. Once there is a refresh token, none of this applies.
 *
 * Reads storage directly rather than going through `getTokens`, for the same
 * reason `setTokens` does: `getTokens` may bootstrap from `?access_token=`,
 * which persists a token and strips the query parameter. Callers ask this from
 * telemetry and error-reporting paths, and a predicate that writes storage and
 * rewrites the address bar is a surprise anywhere, but especially there.
 * @returns {boolean}
 */
export const isBootstrappedSession = () =>
  REFRESHLESS_AUTH_SOURCES.includes(parseStoredTokens()?.auth_source);

/**
 * Whether a stored refresh_token value can actually be used to refresh.
 *
 * IRP sessions and legacy `?access_token=` auto-login links have no refresh
 * token at all, and the bootstrap above stores that as `null`. Some older
 * writes also left the string `'null'` behind. Sending any of these to the
 * refresh endpoint produces a 400, so callers must check before refreshing
 * rather than after.
 * @param {unknown} refreshToken
 * @returns {refreshToken is string}
 */
export const isUsableRefreshToken = refreshToken =>
  typeof refreshToken === 'string' &&
  refreshToken !== '' &&
  refreshToken !== 'null';

/**
 * Saves the Scorpius tokens to localStorage.
 *
 * Carries `auth_source` forward from the stored session when the caller did not
 * supply one and the incoming tokens still have no usable refresh token. The
 * bootstrap in `getTokens` writes the marker, and the IRP entry points then
 * rebuild the token object from the raw token and write it back — so without
 * this the marker is erased microseconds after it is recorded, and nothing that
 * reads it ever sees it.
 *
 * Tied to the refresh token rather than to a list of callers because that is
 * the condition the marker exists to describe: a session with nothing to
 * refresh with. Once real credentials arrive the marker drops on its own, which
 * is what keeps an upgraded session from inheriting a suppression it should not
 * have. Asking each of the nine writers to clear it by hand is the same trap
 * that produced this bug, pointed the other way.
 * @param {Object} tokens
 * @param {string} tokens.token
 * @param {string} tokens.refresh_token
 * @param {string} [tokens.auth_source]
 */
export const setTokens = tokens => {
  const inheritedAuthSource =
    tokens?.auth_source === undefined &&
    !isUsableRefreshToken(tokens?.refresh_token)
      ? parseStoredTokens()?.auth_source
      : undefined;

  localStorage.setItem(
    SCORPIUS_KEY,
    JSON.stringify(
      inheritedAuthSource === undefined
        ? tokens
        : { ...tokens, auth_source: inheritedAuthSource },
    ),
  );
};

/**
 * Deletes the tokens from localStorage.
 */
export const clearTokens = () => {
  localStorage.removeItem(SCORPIUS_KEY);
  localStorage.removeItem(PRODUCT_TOKEN);
};
